import { useCallback, useEffect, useRef, useState } from 'react';

const makeBrownNoise = (ctx, seconds = 3) => {
  const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }
  return buf;
};

export const useAmbience = (enabled = true) => {
  const [muted, setMuted] = useState(false);
  const graph = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);

    // low wind: brown noise through a slowly wandering low-pass
    const wind = ctx.createBufferSource();
    wind.buffer = makeBrownNoise(ctx);
    wind.loop = true;
    const windFilter = ctx.createBiquadFilter();
    windFilter.type = 'lowpass';
    windFilter.frequency.value = 320;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 150;
    lfo.connect(lfoGain);
    lfoGain.connect(windFilter.frequency);
    const windGain = ctx.createGain();
    windGain.gain.value = 0.5;
    wind.connect(windFilter);
    windFilter.connect(windGain);
    windGain.connect(master);
    wind.start();
    lfo.start();

    // ember crackle: sparse high-passed micro-bursts
    let alive = true;
    const crackle = () => {
      if (!alive) return;
      const dur = 0.005 + Math.random() * 0.02;
      const src = ctx.createBufferSource();
      const buf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * dur)), ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
      src.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 1200 + Math.random() * 2200;
      const g = ctx.createGain();
      g.gain.value = 0.02 + Math.random() * 0.07;
      src.connect(hp);
      hp.connect(g);
      g.connect(master);
      src.start();
      setTimeout(crackle, 60 + Math.random() * 380);
    };
    crackle();

    const fadeIn = () => {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0.12, t + 3.5);
    };
    const onGesture = () => ctx.resume().then(fadeIn);
    if (ctx.state === 'suspended') {
      window.addEventListener('pointerdown', onGesture);
      window.addEventListener('keydown', onGesture);
    } else {
      fadeIn();
    }

    graph.current = { ctx, master };

    return () => {
      alive = false;
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0, t + 0.6);
      setTimeout(() => {
        try {
          wind.stop();
          lfo.stop();
          ctx.close();
        } catch {}
      }, 800);
      graph.current = null;
    };
  }, [enabled]);

  const toggleAmbience = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      const g = graph.current;
      if (g) {
        const t = g.ctx.currentTime;
        g.master.gain.cancelScheduledValues(t);
        g.master.gain.setValueAtTime(g.master.gain.value, t);
        g.master.gain.linearRampToValueAtTime(next ? 0 : 0.12, t + 0.5);
      }
      return next;
    });
  }, []);

  return { muted, toggleAmbience };
};
